import axios from 'axios'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

/** Injected by client/tools/deploy.ts; absent in local dev and in tests */
const RUNNING_BUILD_ID = process.env.BUILD_ID

export const VERSION_POLL_INTERVAL_MS = 15 * 60 * 1000
export const VERSION_RECHECK_MIN_INTERVAL_MS = 60 * 1000

type VersionResponse = {
  buildId?: unknown
}

let badPayloadWarned = false

/**
 * A deploy that failed to upload version.json is invisible otherwise: the SPA
 * fallback answers 200 with index.html, so every poll would silently no-op.
 */
const warnOnceAboutBadPayload = () => {
  if (badPayloadWarned) return
  badPayloadWarned = true
  console.warn('version.json did not return a buildId; the update prompt is inactive')
}

export function useAppVersion(): { updateAvailable: boolean; reload: () => void } {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const lastCheckedAt = useRef(0)

  const reload = useCallback(() => window.location.reload(), [])

  useEffect(() => {
    // Local dev and jest have no build id to compare against, so there is
    // nothing meaningful to poll for
    if (!RUNNING_BUILD_ID) return

    let cancelled = false
    const controller = new AbortController()

    const check = async (): Promise<void> => {
      if (cancelled) return
      lastCheckedAt.current = Date.now()

      try {
        // No cache-buster: the distribution does not forward query strings, and
        // the object is served with max-age=0
        const res = await axios.get<VersionResponse>('/version.json', {
          timeout: 5000,
          signal: controller.signal,
        })

        // The distribution turns a missing version.json into 200 + index.html,
        // so the payload shape is the only trustworthy signal here
        const servedBuildId = res.data?.buildId
        if (typeof servedBuildId !== 'string' || !servedBuildId) {
          warnOnceAboutBadPayload()
          return
        }

        if (!cancelled && servedBuildId !== RUNNING_BUILD_ID) setUpdateAvailable(true)
      } catch {
        // A failed check is not worth surfacing: the app keeps working on the
        // build it already has, and the next poll will try again
      }
    }

    const interval = window.setInterval(() => void check(), VERSION_POLL_INTERVAL_MS)

    // A tab parked for weeks fires almost no timers; coming back into view is
    // the moment that actually matters
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastCheckedAt.current < VERSION_RECHECK_MIN_INTERVAL_MS) return
      void check()
    }

    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      controller.abort()
      window.clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  return useMemo(() => ({ updateAvailable, reload }), [updateAvailable, reload])
}
